/**
 * EstateCipSection — Probate-court CIP fields for Estate entity dialog.
 *
 * Per docs/port-design/category-cards/estate.md §"Identity / CIP semantics"
 * and people-form-port.md §13d:
 *
 *   - Letters variant radio — Executor, Administrator, Affidavit
 *   - Field 254 (IdVerifyCode):
 *       Executor / Administrator → '6' (State Documents, case exists)
 *       Affidavit                → '8' (Other, no formal case) — per Bill's
 *         D default ("No Fields if No Case, DOB is date of death")
 *     TODO(bill): confirm §13d CIP field value — defaulted per card.
 *   - Field 255 (IdNumber): Case Number OR blank (Affidavit)
 *   - Field 256 (IdIssueBy): state/county (already captured in Identity section;
 *     we display the composite here for confirmation)
 *   - Field 257 (IdIssueDate): Letters issue date / filing date
 *   - Field 258 (IdExpiryDate): blank — letters don't expire
 *
 * Also surfaces the hard-required upload slots for Letters Testamentary AND
 * Letters of Administration. Seed SQL Notes field already states these are
 * OR-alternatives, so the DocumentSlotsSection renders both but the
 * derivation engine flags supervisor-approval if neither (or the correct
 * one for the selected variant) is uploaded.
 *
 * The actual upload slots live in DocumentSlotsSection at the dialog root —
 * this component focuses on the CIP metadata + the variant chooser + the
 * Case.Net lookup button.
 */

import { Button } from '@/components/primitives';
import { useStateGovLink } from '../shared/useStateGovLink';
import type { RaoPerson } from '../../../types';
import {
  ESTATE_ID_VERIFY_CODE_BY_VARIANT,
  ESTATE_LETTERS_VARIANTS,
  ESTATE_HELPER_TEXT,
  type EstateLettersVariant,
} from './estate-config';

// -----------------------------------------------------------------------------
// Styles
// -----------------------------------------------------------------------------

const sectionWrapper: React.CSSProperties = {
  padding: 12,
  border: '1px solid var(--ink-200)',
  borderRadius: 6,
  background: 'var(--surface-subtle, #fafbfc)',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

const sectionHeader: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 0.8,
  color: 'var(--pcb-blue, #2968ad)',
};

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--ink-600)',
  display: 'block',
  marginBottom: 4,
};

const helperStyle: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--ink-600)',
  marginTop: 2,
  lineHeight: 1.4,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 4,
  border: '1px solid var(--ink-200)',
  fontSize: 14,
  boxSizing: 'border-box',
};

const readOnlyStyle: React.CSSProperties = {
  ...inputStyle,
  background: 'var(--field-readonly-bg, #f5f5f5)',
  color: 'var(--ink-700)',
};

function changedFieldStyle(base: React.CSSProperties, changed: boolean): React.CSSProperties {
  return changed
    ? { ...base, borderLeft: '3px solid var(--pcb-blue, #2968ad)', background: 'var(--field-changed-bg, #f0f7ff)', color: 'var(--ink-900, #1d1d1f)' }
    : base;
}

// -----------------------------------------------------------------------------
// Props
// -----------------------------------------------------------------------------

export interface EstateCipSectionProps {
  person: RaoPerson;
  onChange: (patch: Partial<RaoPerson>) => void;
  isChanged?: (field: string) => boolean;
  readOnly?: boolean;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function EstateCipSection({
  person,
  onChange,
  isChanged,
  readOnly = false,
}: EstateCipSectionProps): JSX.Element {
  const ic = isChanged ?? ((_: string) => false);

  const variant: EstateLettersVariant =
    (typeof person.extra?.lettersVariant === 'string'
      ? (person.extra.lettersVariant as EstateLettersVariant)
      : 'Executor');

  const caseState =
    typeof person.extra?.idState === 'string' ? (person.extra.idState as string) : 'MO';
  const caseNumber =
    typeof person.extra?.idNumber === 'string' ? (person.extra.idNumber as string) : '';
  const caseCounty =
    typeof person.extra?.idCounty === 'string' ? (person.extra.idCounty as string) : '';
  const filingDate =
    typeof person.extra?.idIssueDate === 'string' ? (person.extra.idIssueDate as string) : '';
  const closureDate =
    typeof person.extra?.idExpiryDate === 'string' ? (person.extra.idExpiryDate as string) : '';

  // IdVerifyCode (Field 254) — auto-set by variant per card §13d defaults.
  // TODO(bill): confirm §13d CIP field value — using card defaults for now.
  const idVerifyCode = ESTATE_ID_VERIFY_CODE_BY_VARIANT[variant];

  const updateExtra = (patch: Record<string, unknown>): void => {
    onChange({ extra: { ...(person.extra ?? {}), ...patch } });
  };

  const handleVariantChange = (next: EstateLettersVariant): void => {
    // When flipping to Affidavit, auto-clear case-number fields per Bill's
    // D default ("No Fields if No Case"). Officer can re-populate if needed.
    if (next === 'Affidavit') {
      updateExtra({
        lettersVariant: next,
        idNumber: '',
        idVerifyCode: ESTATE_ID_VERIFY_CODE_BY_VARIANT.Affidavit,
      });
    } else {
      updateExtra({
        lettersVariant: next,
        idVerifyCode: ESTATE_ID_VERIFY_CODE_BY_VARIANT[next],
      });
    }
  };

  // Case.Net lookup — opens the MO state court search page.
  // Falls back silently if StateGovSearchLinks hasn't seeded the row.
  const stateLink = useStateGovLink(caseState || 'MO', 'CourtRecords');

  const compositeIssueBy = [caseState, caseCounty].filter((s) => !!s).join(' · ');

  return (
    <div style={sectionWrapper}>
      <div style={sectionHeader}>CIP / Probate Court</div>

      {/* Letters variant radio */}
      <div>
        <label style={labelStyle}>Letters variant</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {ESTATE_LETTERS_VARIANTS.map((opt) => (
            <label
              key={opt.value}
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'flex-start',
                padding: '8px 10px',
                border:
                  variant === opt.value
                    ? '1px solid var(--pcb-blue, #2968ad)'
                    : '1px solid var(--ink-200)',
                borderRadius: 6,
                background: variant === opt.value ? 'var(--field-changed-bg, #f0f7ff)' : 'var(--surface-card, #fff)',
                cursor: readOnly ? 'not-allowed' : 'pointer',
              }}
            >
              <input
                type="radio"
                name="rao-est-variant"
                value={opt.value}
                checked={variant === opt.value}
                onChange={() => handleVariantChange(opt.value)}
                disabled={readOnly}
                style={{ marginTop: 3 }}
              />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{opt.label}</div>
                <div style={{ fontSize: 11, color: 'var(--ink-600)', lineHeight: 1.4 }}>
                  {opt.helperText}
                </div>
              </div>
            </label>
          ))}
        </div>
        <div style={helperStyle}>{ESTATE_HELPER_TEXT.lettersTestamentaryVsAdministration}</div>
      </div>

      {/* Case.Net lookup button (active when variant is NOT Affidavit + link seeded) */}
      {variant !== 'Affidavit' && stateLink.link && (
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => stateLink.openInBrowser()}
            disabled={readOnly}
            title={`Look up ${caseState || 'MO'} probate case on the state court search site`}
          >
            Lookup in {caseState || 'MO'} Case.Net
          </Button>
        </div>
      )}

      {/* Field 254 (IdVerifyCode) read-only display + Field 255 (case number) */}
      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 12 }}>
        <div>
          <label style={labelStyle}>ID Verify Code (Field 254)</label>
          <input
            type="text"
            value={
              idVerifyCode === '6'
                ? '6 · State Documents'
                : idVerifyCode === '8'
                  ? '8 · Other'
                  : idVerifyCode
            }
            disabled
            style={readOnlyStyle}
            title="Auto-set by Letters variant — see card §13d defaults"
          />
          <div style={helperStyle}>
            {variant === 'Affidavit'
              ? 'Small-estate path — defaulted to 8 (Other).'
              : 'Case exists — defaulted to 6 (State Documents).'}
            {' '}
            {/* TODO(bill): confirm §13d CIP field value */}
          </div>
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-est-cip-case">Case number (Field 255)</label>
          <input
            id="rao-est-cip-case"
            type="text"
            value={caseNumber}
            onChange={(e) => updateExtra({ idNumber: e.currentTarget.value })}
            readOnly={readOnly || variant === 'Affidavit'}
            style={changedFieldStyle(
              readOnly || variant === 'Affidavit' ? readOnlyStyle : inputStyle,
              ic('idNumber'),
            )}
            placeholder={variant === 'Affidavit' ? '(blank — small-estate)' : 'e.g., 23PH-PR00123'}
            title={
              variant === 'Affidavit'
                ? 'Case number blank for small-estate affidavit'
                : 'Probate court case number'
            }
          />
        </div>
      </div>

      {/* Field 256 (composite issue-by) + Field 257 (filing date) + 258 (closure) */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <div>
          <label style={labelStyle}>Issued by (Field 256)</label>
          <input
            type="text"
            value={compositeIssueBy}
            disabled
            style={readOnlyStyle}
            title="State + county composite from Identity section"
          />
          <div style={helperStyle}>Composed from case state + county above.</div>
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-est-filing-date">
            Filing / issue date (Field 257)
          </label>
          <input
            id="rao-est-filing-date"
            type="date"
            value={filingDate}
            onChange={(e) => updateExtra({ idIssueDate: e.currentTarget.value })}
            readOnly={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('idIssueDate'))}
            title="Date the court issued the Letters (or affidavit date)"
          />
          <div style={helperStyle}>{ESTATE_HELPER_TEXT.filingDate}</div>
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-est-closure-date">
            Closure date (Field 258)
          </label>
          <input
            id="rao-est-closure-date"
            type="date"
            value={closureDate}
            onChange={(e) => updateExtra({ idExpiryDate: e.currentTarget.value })}
            readOnly={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('idExpiryDate'))}
            title="Estate closure date — optional, letters don't expire"
          />
          <div style={helperStyle}>{ESTATE_HELPER_TEXT.closureDate}</div>
        </div>
      </div>

      {/*
        Hard-required upload slots for Letters are rendered in the
        DocumentSlotsSection at the dialog root. The slot labels come from the
        EntityRuleRequiredDocs SQL seed (LETTERS_TESTAMENTARY and
        LETTERS_ADMINISTRATION rows). We surface a reminder here so officer
        understands the CIP flow expects an upload.
      */}
      <div
        style={{
          padding: '8px 12px',
          background: 'var(--info-bg)',
          border: '1px solid var(--pcb-blue)',
          borderRadius: 6,
          fontSize: 12,
          color: 'var(--ink-900)',
          lineHeight: 1.45,
        }}
      >
        <strong>Upload required:</strong>{' '}
        {variant === 'Affidavit'
          ? 'Small-estate affidavit document. (Scroll to Document Requirements below.)'
          : variant === 'Executor'
            ? 'Letters Testamentary. Letters of Administration is the OR-alternative when variant flips to court-appointed. (Scroll to Document Requirements below.)'
            : 'Letters of Administration. Letters Testamentary is the OR-alternative when variant flips to executor-appointed. (Scroll to Document Requirements below.)'}
      </div>
    </div>
  );
}
